import LandingNavbar, {
  LandingFooter,
} from "@/features/landing/components/LandingNavbar";
import TableOfContents, {
  type TableOfContentsSection,
} from "@/components/Common/TableOfContents";
import { useEffect, useState } from "react";
import gsap from "gsap";
import ScrollToPlugin from "gsap/ScrollToPlugin";

const privacySections: TableOfContentsSection[] = [
  {
    title: "Introduction",
    id: "introduction",
  },
  {
    title: "Information Metawhale Collects",
    items: [
      { label: "Information You Provide", id: "information-you-provide" },
      {
        label: "Personal Information Received Automatically",
        id: "personal-information-received-automatically",
      },
      { label: "Media Monitoring Data", id: "media-monitoring-data" },
    ],
  },
  {
    title: "How Metawhale Uses Personal Information",
    id: "how-we-use",
  },
  {
    title: "Disclosure and Sharing of Personal Information",
    id: "disclosure-sharing",
  },
  {
    title: "User's Rights",
    id: "users-rights",
  },
  {
    title: "Data Security and Retention",
    id: "data-security",
  },
  {
    title: "GDPR Information",
    id: "gdpr",
  },
  {
    title: "Philippine Data Privacy Act",
    id: "ph-privacy-act",
  },
  {
    title: "Privacy Policy Updates",
    id: "policy-updates",
  },
  {
    title: "Contact Us",
    id: "contact-us",
  },
];

export default function PrivacyPolicyContainer() {
  const [isScrolled, setIsScrolled] = useState(false);

  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    const scroller = document.querySelector(".privacy-scroll");
    if (!scroller) return;

    const onScroll = () => {
      setIsScrolled(scroller.scrollTop > 10);
      setScrollTop(scroller.scrollTop);
    };

    scroller.addEventListener("scroll", onScroll);
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  gsap.registerPlugin(ScrollToPlugin);

  useEffect(() => {
    const scroller = document.querySelector(".privacy-scroll");
    if (!scroller) return;

    const onScroll = () => {
      setIsScrolled(scroller.scrollTop > 10);
    };

    scroller.addEventListener("scroll", onScroll);

    return () => {
      scroller.removeEventListener("scroll", onScroll);
    };
  }, []);
  return (
    <div className="privacy-scroll scrollbar-thin relative flex h-screen w-full flex-col overflow-x-hidden overflow-y-auto scroll-smooth bg-neutral-50">
      <LandingNavbar isScrolled={isScrolled} getInTouch={true} />

      <div className="-mt-[80px] flex w-full flex-1 transition-all duration-300">
        <div className="pointer-events-none fixed top-5 left-70">
          <div
            className="h-[700px] w-[200px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, #EA580CBF 0%, transparent 100%)",
              filter: "blur(120px)",
              opacity: "25%",
            }}
          />
        </div>
        {/* Main Content */}
        <div className="flex-1 space-y-20 px-[400px] py-[200px]">
          <div className="w-full space-y-10">
            <h1 className="text-7xl leading-20 font-extrabold -tracking-wide text-neutral-950">
              Privacy Policy
            </h1>
            <div className="text-medium text-md w-full max-w-[640px] space-y-6 leading-normal text-neutral-800">
              <p>
                Your privacy matters to us. This policy explains how Metawhale
                collects, uses, and protects your information when you use our
                media monitoring services.
              </p>
              <p>
                <span className="font-bold">Last Updated</span>: January 1, 2026
              </p>
            </div>
          </div>
          <div className="w-full max-w-[640px] space-y-6">
            <section id="introduction" className="space-y-1">
              <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                Introduction
              </h3>
              <p className="text-sm leading-[140%] font-medium text-neutral-950">
                Welcome to Metawhale! We value your privacy and are committed to
                safeguarding your personal information. This Privacy Policy
                outlines how we collect, use, share, and protect your
                information when you interact with our platform.
                <br /> <br />
                Metawhale is a media intelligence platform that enables users to
                monitor, extract, and analyze media content across Print,
                Online, TV, Radio, and Social Media. Our platform provides
                keyword-based, influencer-based, and source-based tracking using
                a unified dashboard with duration-based results. We help
                businesses and organizations track brand mentions, monitor
                industry trends, and gather media insights across the Philippine
                market and beyond.
                <br /> <br />
                Please note that while this Policy addresses the collection and
                handling of Personal Information within the Metawhale ecosystem,
                it does not extend to content processed on behalf of our
                enterprise customers or through our API, which are governed by
                individual agreements with those customers.
                <br /> <br />
                As part of Metawhale's functionality, you have the option to
                connect your account with various third-party applications and
                social media platforms. Please be aware that the collection of
                your information by these third parties is governed by their
                respective privacy policies and terms. We strongly encourage you
                to review these policies, as Metawhale does not assume
                responsibility for the actions or policies of third-party
                platforms.
                <br /> <br />
                We emphasize our commitment to transparency regarding the use of
                your data to enhance our platform. We respect your privacy and
                aim to empower you with choices and control over your
                information.
              </p>
            </section>
            <section id="information-collected" className="space-y-5">
              <div className="space-y-1">
                <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                  Information Metawhale Collects
                </h3>
                <p className="text-sm leading-[140%] font-medium text-neutral-950">
                  We collect information to enhance our platform and tailor it
                  to your needs. By using our platform, you consent to the
                  collection and processing of your Personal Information as
                  described herein.
                </p>
              </div>
              <section className="space-y-6 pl-4">
                <div className="space-y-1">
                  <h1
                    id="information-you-provide"
                    className="text-[18px] leading-5 font-bold -tracking-wide text-neutral-950"
                  >
                    A. Information You Provide
                  </h1>
                  <p className="text-sm leading-[140%] font-medium text-neutral-950">
                    When you engage with our platform, either by creating an
                    account or communicating with us, we collect the following
                    Personal Information:
                  </p>
                  <ul className="list-disc pt-3 pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Account Information: &nbsp;
                      </span>
                      Upon creating an account, we collect details like account
                      credentials, such as email address, password, company
                      name, job title, and contact information. we collect
                      details like account credentials, such as email address,
                      password, company name, job title, and contact
                      information.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Third-Party Login Integration: &nbsp;
                      </span>
                      You may log into your account using third-party
                      applications like Google. We collect your name and email
                      address from the respective third-party application to
                      authenticate and verify your identity.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Monitoring Configuration: &nbsp;
                      </span>
                      Information you provide to configure your monitoring
                      preferences, including keywords, influencer URLs, media
                      source selections, country preferences, language settings,
                      and date ranges.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Billing Information: &nbsp;
                      </span>
                      Payment details, billing address, and transaction history
                      for paid subscriptions.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Communication Information: &nbsp;
                      </span>
                      Any correspondence you initiate with us leads to the
                      collection of your name, contact information, and message
                      contents.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Other Information You Provide: &nbsp;
                      </span>
                      We also collect other information you may provide,
                      including participation in events or surveys, or
                      information necessary for identity verification.
                    </li>
                  </ul>
                </div>
                <div
                  id="personal-information-received-automatically"
                  className="space-y-1"
                >
                  <h1 className="text-[18px] leading-5 font-bold -tracking-wide text-neutral-950">
                    B. Personal Information Received Automatically
                  </h1>
                  <p className="text-sm leading-[140%] font-medium text-neutral-950">
                    Through your interaction with our platform, we automatically
                    receive the following Technical Information:
                  </p>
                  <ul className="list-disc pt-3 pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Log Data: &nbsp;
                      </span>
                      Automatically transmitted by your browser or device,
                      encompassing your Internet Protocol ("IP") address,
                      browser type and settings, request timestamp, and
                      interaction patterns with our platform.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Usage Data: &nbsp;
                      </span>
                      Information regarding your usage patterns, such as feature
                      utilization, search queries, reports generated, time zone,
                      geographic location, access dates, and device type.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Device Information: &nbsp;
                      </span>
                      Details about your device, including its name, operating
                      system, identifiers, and browser type.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Cookies: &nbsp;
                      </span>
                      We use cookies to enhance the performance and
                      functionality of our platform. Most web browsers can be
                      set to disable the use of cookies. However, if you disable
                      cookies, you may not be able to access certain
                      functionalities correctly.
                    </li>
                  </ul>
                </div>
                <div id="media-monitoring-data" className="space-y-1">
                  <h1 className="text-[18px] leading-5 font-bold -tracking-wide text-neutral-950">
                    C. Media Monitoring Data
                  </h1>
                  <p className="text-sm leading-[140%] font-medium text-neutral-950">
                    Our platform collects and processes publicly available media
                    content from Print, Online, TV, Radio, and Social Media
                    sources. This includes:
                  </p>
                  <ul className="list-disc py-3 pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>
                      News articles, editorials, and opinion pieces from
                      newspapers and magazines
                    </li>
                    <li>
                      Online news articles, blog posts, and forum discussions
                    </li>
                    <li>TV and radio broadcast transcripts and metadata</li>
                    <li>
                      Publicly available social media posts and engagement
                      metrics
                    </li>
                  </ul>
                  <p className="text-sm leading-[140%] font-medium text-neutral-950">
                    This data is collected to provide our monitoring services
                    and consists of publicly available information. We do not
                    collect private or non-public content.
                  </p>
                </div>
              </section>
            </section>
            <section id="how-we-use" className="space-y-5">
              <div className="space-y-1">
                <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                  How Metawhale Uses Personal Information
                </h3>
                <p className="text-sm leading-[140%] font-medium text-neutral-950">
                  At Metawhale, we prioritize the protection and confidentiality
                  of your personal information. We utilize Personal Information
                  for the following purposes:
                </p>
              </div>
              <section id="purposes" className="space-y-6 pl-4">
                <div className="space-y-1">
                  <ul className="list-disc pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Service Provision and Enhancement: &nbsp;
                      </span>
                      Personal Information enables us to provide, administer,
                      maintain, and analyze our Services effectively. This
                      includes delivering keyword monitoring results, influencer
                      tracking data, and source-based media coverage to your
                      dashboard.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Improvement and Research: &nbsp;
                      </span>
                      We use Personal Information to improve our Services
                      continuously and conduct research aimed at enhancing user
                      satisfaction and meeting evolving needs.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Communication: &nbsp;
                      </span>
                      Personal Information facilitates communication with our
                      users, including sending monitoring alerts, service
                      updates, and relevant information to ensure informed
                      engagement.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        API Access: &nbsp;
                      </span>
                      We use your information to generate and manage API keys
                      for programmatic access to monitoring results.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Fraud Prevention and Security: &nbsp;
                      </span>
                      Personal Information is instrumental in preventing fraud,
                      criminal activity, and misuse of our Services, ensuring
                      the security of our IT systems, architecture, and
                      networks.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Business Transfers: &nbsp;
                      </span>
                      In the event of business transfers, Personal Information
                      may be utilized for seamless transition and continuity of
                      service provision.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Legal Compliance and Protection: &nbsp;
                      </span>
                      We use Personal Information to comply with legal
                      obligations, legal processes, and to safeguard the rights,
                      privacy, safety, or property of Metawhale, our affiliates,
                      users, or other third parties.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Aggregated or De-Identified Data: &nbsp;
                      </span>
                      We may aggregate or de-identify Personal Information for
                      analysis, feature enhancement, research, and similar
                      purposes.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Customer Support: &nbsp;
                      </span>
                      Personal Information is used to provide customer support
                      and to respond to your inquiries, requests, or complaints.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Children's Privacy: &nbsp;
                      </span>
                      Our services are not intended for individuals under the
                      age of 18. We do not knowingly collect personally
                      identifiable information from anyone under the age of 18.
                      If we become aware that we have collected personal data
                      from anyone under this age, we take steps to remove that
                      information from our servers.ith trusted third-party
                      vendors who assist us in operating our platform,
                      processing payments, or providing customer support, under
                      strict confidentiality agreements.
                    </li>
                  </ul>
                </div>
              </section>
              <p className="text-sm leading-[140%] font-medium text-neutral-950">
                Metawhale is committed to transparent data practices and does
                not sell, trade, or share personal information with third
                parties, except as necessary for service provision, legal
                compliance, or as described in this Policy.
              </p>
            </section>
            <section id="disclosure-sharing" className="space-y-5">
              <div className="space-y-1">
                <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                  Disclosure and Sharing of Personal Information
                </h3>
                <p className="text-sm leading-[140%] font-medium text-neutral-950">
                  We disclose your information to third parties for various
                  business purposes, including to provide our services, to
                  protect us or others, or in the event of a major business
                  transaction such as a merger, sale, or asset transfer, as
                  described below.
                </p>
              </div>
              <section id="parties" className="space-y-6 pl-4">
                <div className="space-y-1">
                  <ul className="list-disc pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Vendors and Service Providers: &nbsp;
                      </span>
                      To facilitate our business operations and perform certain
                      services, we may share Personal Information with vendors
                      and service providers. These may include hosting services,
                      cloud services, payment processors, and other information
                      technology providers. These parties will access, process,
                      or store Personal Information solely for the purpose of
                      fulfilling their duties to us.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Media Data Providers: &nbsp;
                      </span>
                      We work with media data providers and content aggregators
                      (such as news crawlers, social media APIs, and broadcast
                      transcription services) to deliver comprehensive
                      monitoring coverage. These providers operate under strict
                      data handling agreements.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Disclosures to Protect Us or Others: &nbsp;
                      </span>
                      We may access, preserve, and disclose any information we
                      store associated with you to external parties if we, in
                      good faith, believe doing so is required or appropriate
                      to: comply with law enforcement or national security
                      requests and legal processes; protect your, our, or
                      others' rights, property, or safety; enforce our policies
                      or contracts; or assist with an investigation or
                      prosecution of suspected or actual illegal activity.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Business Transfers: &nbsp;
                      </span>
                      If we are involved in strategic transactions,
                      reorganization, bankruptcy, receivership, or transitioning
                      services to another provider, your Personal Information
                      may be disclosed during the diligence process and
                      transferred to a successor or affiliate as part of that
                      Transaction.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Legal Requirements: &nbsp;
                      </span>
                      We may share your Personal Information with government
                      authorities, industry peers, or other third parties if
                      required by law or in good faith belief to comply with a
                      legal obligation, protect and defend our rights or
                      property, prevent fraud or illegal activity, or ensure the
                      safety and integrity of our products, employees, users, or
                      the public.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Affiliates: &nbsp;
                      </span>
                      Personal Information may be disclosed to our affiliates,
                      entities that control, are controlled by, or are under
                      common control with Metawhale. Our affiliates will use the
                      shared Personal Information in accordance with this
                      Privacy Policy.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Business Account Administrators: &nbsp;
                      </span>
                      In Business Accounts, the administrators of that account
                      may access and control your Metawhale Account.
                      Additionally, if you create an account using an email
                      address belonging to your employer or another
                      organization, we may share certain account information
                      with your employer or organization.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        International Data Transfers: &nbsp;
                      </span>
                      If your Personal Information is transferred to a country
                      outside your country of residence, we will ensure that
                      such transfers are conducted in compliance with applicable
                      data protection laws and appropriate safeguards are in
                      place.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Anonymized Data: &nbsp;
                      </span>
                      We may share anonymized or aggregated data with third
                      parties for research, marketing, analytics, and other
                      purposes, provided that such data does not identify you
                      personally.
                    </li>
                  </ul>
                </div>
              </section>
              <p className="text-sm leading-[140%] font-medium text-neutral-950">
                By using Metawhale, you acknowledge and agree to the disclosure
                and sharing of your Personal Information as outlined above, in
                accordance with this Privacy Policy.
              </p>
            </section>
            <section id="users-rights" className="space-y-5">
              <div className="space-y-1">
                <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                  User's Rights
                </h3>
                <p className="text-sm leading-[140%] font-medium text-neutral-950">
                  At Metawhale, we respect your rights concerning your Personal
                  Information. Depending on your location and applicable laws,
                  you may have certain statutory rights, including but not
                  limited to:
                </p>
              </div>
              <section id="rights-and-obligations" className="space-y-6 pl-4">
                <div className="space-y-1">
                  <ul className="list-disc pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Access: &nbsp;
                      </span>
                      You have the right to access your Personal Information and
                      receive information regarding how it is processed.
                    </li>

                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Deletion: &nbsp;
                      </span>
                      You can request the deletion of your Personal Information
                      from our records, subject to legal obligations and
                      legitimate business interests.
                    </li>

                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Rectification: &nbsp;
                      </span>
                      You have the right to rectify or update your Personal
                      Information if it is inaccurate or incomplete.
                    </li>

                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Data Portability: &nbsp;
                      </span>
                      You may request the transfer of your Personal Information
                      to a third party in a structured, commonly used, and
                      machine-readable format, where technically feasible.
                    </li>

                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Restriction: &nbsp;
                      </span>
                      You have the right to restrict how we process your
                      Personal Information in certain circumstances, such as
                      contesting its accuracy or objecting to its processing.
                    </li>

                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Consent Withdrawal: &nbsp;
                      </span>
                      If we rely on your consent as the legal basis for
                      processing your Personal Information, you have the right
                      to withdraw that consent at any time.
                    </li>

                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Objection: &nbsp;
                      </span>
                      You can object to how we process your Personal
                      Information, including for direct marketing purposes or
                      where we process it based on legitimate interests.
                    </li>

                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Complaints: &nbsp;
                      </span>
                      If you believe that your rights regarding your Personal
                      Information have been violated, the involved parties
                      commit to seeking resolution through good-faith
                      negotiation or amicable means. You may also lodge a
                      complaint with your local data protection authority.
                    </li>

                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Right to Information: &nbsp;
                      </span>
                      You have the right to be informed about the collection and
                      use of your Personal Information, including the purposes
                      for processing your data, how long your data will be
                      retained, and who it will be shared with.
                    </li>

                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Data Breach Notification: &nbsp;
                      </span>
                      In the event of a data breach that is likely to result in
                      a high risk to your rights and freedoms, we will notify
                      you and the appropriate supervisory authority without
                      undue delay.
                    </li>
                  </ul>
                </div>
              </section>

              <p className="text-sm leading-[140%] font-medium text-neutral-950">
                We are committed to safeguarding your privacy and ensuring that
                your Personal Information is not sold or shared for
                cross-contextual behavioral advertising purposes. Your trust is
                essential to us, and we are dedicated to upholding your privacy
                rights in accordance with relevant regulations and best
                practices.
              </p>
            </section>{" "}
            <section id="data-security" className="space-y-5">
              <div className="space-y-1">
                <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                  Data Security and Retention
                </h3>
                <p className="text-sm leading-[140%] font-medium text-neutral-950">
                  We employ commercially reasonable technical, administrative,
                  and organizational measures to safeguard your data, aiming to
                  prevent loss, misuse, unauthorized access, disclosure,
                  alteration, or destruction. However, it's important to note
                  that no Internet or email transmission can be guaranteed to be
                  entirely secure or error-free.
                  <br />
                  <br />
                  Our security measures include:
                </p>
              </div>
              <section id="security-measures" className="space-y-6 pl-4">
                <div className="space-y-1">
                  <ul className="list-disc pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>Encryption of data in transit and at rest</li>
                    <li>
                      Regular security audits and vulnerability assessments
                    </li>
                    <li>
                      Strict access controls limiting access to authorized
                      personnel only
                    </li>
                    <li>Employee training on data protection practices</li>
                    <li>
                      Incident response procedures for prompt breach response
                    </li>
                  </ul>
                </div>
              </section>

              <p className="text-sm leading-[140%] font-medium text-neutral-950">
                Regarding the retention of your Personal Information, we will
                retain it only for as long as necessary to fulfill the purposes
                for which it was collected, including:
              </p>

              <section className="space-y-6 pl-4">
                <div className="space-y-1">
                  <ul className="list-disc pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Account Data: &nbsp;
                      </span>
                      Retained while your account is active and for a reasonable
                      period thereafter for legal and business purposes.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Monitoring Data: &nbsp;
                      </span>
                      Media monitoring results are retained according to your
                      subscription plan (typically 30 days to 1 year).
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Billing Records: &nbsp;
                      </span>
                      Retained for 7 years to comply with tax and accounting
                      accounting requirements.
                    </li>
                    <li>
                      <span className="text-sm leading-[140%] font-bold tracking-normal text-neutral-950">
                        Communication Records: &nbsp;
                      </span>
                      Retained for 2 years after resolution for quality
                      assurance and dispute resolution.
                    </li>
                  </ul>
                </div>
              </section>

              <p className="text-sm leading-[140%] font-medium text-neutral-950">
                When your Personal Information is no longer needed, we ensure
                its secure disposal or anonymization to prevent unauthorized
                access or use.
              </p>
            </section>
            <section id="gdpr" className="space-y-5">
              <div id="compliance" className="space-y-1">
                <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                  Information about General Data Protection Regulation (GDPR)
                </h3>
                <p className="text-sm leading-[140%] font-medium text-neutral-950">
                  If you are from the European Economic Area (EEA), we may be
                  collecting and using information from you. The General Data
                  Protection Regulation (GDPR) is an EU-wide privacy and data
                  protection law that regulates how companies protect the
                  personal data of EU residents.
                  <br />
                  <br />
                  The GDPR is relevant to any globally operating company, not
                  just EU-based businesses and EU residents. We value our
                  customers' data irrespective of their location, which is why
                  we have implemented GDPR controls as our baseline standard for
                  all our operations worldwide.
                  <br />
                  <br />
                  We strongly believe that your data privacy is very important.
                  We already have robust security and privacy practices in place
                  that meet or exceed the requirements of this regulation.
                </p>
              </div>
            </section>
            <section id="ph-privacy-act" className="space-y-5">
              <div className="space-y-1">
                <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                  Philippine Data Privacy Act
                </h3>
                <p className="text-sm leading-[140%] font-medium text-neutral-950">
                  Metawhale is committed to compliance with the Philippine Data
                  Privacy Act of 2012 (Republic Act No. 10173) and its
                  Implementing Rules and Regulations. As a platform serving the
                  Philippine market, we ensure that:
                </p>
              </div>
              <section className="space-y-6 pl-4">
                <div className="space-y-1">
                  <ul className="list-disc pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>
                      Personal data is collected for specified and legitimate
                      purposes
                    </li>
                    <li>
                      Processing is done with the consent of the data subject or
                      as otherwise permitted by law
                    </li>
                    <li>
                      Data subjects can exercise their rights to access,
                      correct, and object to processing
                    </li>
                    <li>
                      Appropriate security measures are in place to protect
                      personal data
                    </li>
                    <li>
                      Data breach notification procedures comply with NPC
                      requirements
                    </li>
                  </ul>
                </div>
              </section>

              <p className="text-sm leading-[140%] font-medium text-neutral-950">
                Philippine residents may exercise their rights under the Data
                Privacy Act by contacting us using the information provided
                below.
              </p>
            </section>
            <section id="policy-updates" className="space-y-5">
              <div className="space-y-1">
                <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                  Privacy Policy Updates
                </h3>
                <p className="text-sm leading-[140%] font-medium text-neutral-950">
                  We are committed to maintaining transparency and ensuring that
                  you are informed about how we handle your Personal
                  Information. To achieve this, we may update this Privacy
                  Policy periodically to reflect changes in our Services,
                  applicable laws, and other relevant factors.
                  <br />
                  <br />
                  When we make changes, we will:
                </p>
              </div>
              <section className="space-y-6 pl-4">
                <div className="space-y-1">
                  <ul className="list-disc pl-8 text-sm leading-[140%] font-medium tracking-normal text-neutral-950">
                    <li>Post the revised Policy on our website</li>
                    <li>
                      Update the "Effective Date" to indicate the most recent
                      version
                    </li>
                    <li>
                      Notify you of significant changes by email or through our
                      platform
                    </li>
                  </ul>
                </div>
              </section>

              <p className="text-sm leading-[140%] font-medium text-neutral-950">
                We encourage you to review this Policy whenever you access or
                use our Services to stay informed about our information
                practices and the choices available to you. Your continued use
                of our Services after any changes to this Policy signifies your
                acceptance of the updated terms.
              </p>
            </section>
            <section id="contact-us" className="space-y-5">
              <div className="space-y-1">
                <h3 className="text-lg leading-6 font-bold -tracking-wide text-neutral-950">
                  Contact Us
                </h3>
                <p className="text-sm leading-[140%] font-medium text-neutral-950">
                  If you have any questions or concerns about this Privacy
                  Policy, please contact our support team at info@metawhale.com.
                  We are here to assist you and ensure that your privacy-related
                  inquiries are addressed promptly and thoroughly.
                  <br />
                  <br />
                  For complex privacy issues or if you need to discuss any
                  matter in detail, you can request to speak with our designated
                  Data Protection Officer.
                </p>
              </div>
            </section>
          </div>
        </div>

        <div
          className="sticky right-50 h-fit w-[300px] flex-shrink-0 overflow-y-auto rounded-[12px] bg-neutral-100 p-6"
          style={{
            top: `${Math.max(100, 200 - scrollTop)}px`,
            transition: "top 0.25s ease-out",
          }}
        >
          <TableOfContents sections={privacySections} scrollOffset={80} />
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}
